import React from "react";
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  Chip,
  Tooltip,
  alpha
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import {
  capitalizeFirstLetter,
  formatDashedDateWithYear
} from "../../../utils/hooks/dataHandler";

const DEFAULT_USER_CARD_CONFIG = {
  avatarSize: 40,
  contentPadding: 3,
  footerPaddingTop: 1.5,
  height: 220,
  nameLineClamp: 2,
  topGap: 1.5
};

const UserCard = ({
  displayConfig = DEFAULT_USER_CARD_CONFIG,
  user,
  typeConfig,
  onClick
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "banned":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "pending":
        return "Pending";
      default:
        return status;
    }
  };

  const status = user.status || user.user_auth?.status;
  const isSubsidiary =
    user.source === "subsidiary" || user.user_auth?.user_type === "subsidiary";
  const expiryDate = user.user_auth?.expire_date;
  const IconComponent = typeConfig?.icon;
  const typeLabel =
    typeConfig?.label
      ?.replace(/member/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim() || "";

  return (
    <Card
      sx={{
        height: displayConfig.height,
        borderRadius: 2,
        background: "white",
        border: "1px solid",
        borderColor: "divider",
        transition: "all 0.2s ease",
        cursor: "pointer",
        position: "relative",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: (theme) =>
            `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
          borderColor: "primary.main"
        }
      }}
      onClick={onClick}
    >
      <CardContent
        sx={{
          height: "100%",
          p: displayConfig.contentPadding,
          "&:last-child": {
            pb: displayConfig.contentPadding
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minWidth: 0,
            position: "relative"
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "space-between",
              mb: displayConfig.topGap,
              minWidth: 0
            }}
          >
            <Avatar
              sx={{
                width: displayConfig.avatarSize,
                height: displayConfig.avatarSize,
                borderRadius: 1.5,
                bgcolor: (theme) =>
                  status === "active"
                    ? alpha(theme.palette.primary.main, 0.12)
                    : "grey.300",
                color: status === "active" ? "primary.main" : "grey.600",
                fontSize: "1rem",
                fontWeight: 600,
                flexShrink: 0
              }}
            >
              {user.name ? user.name.charAt(0).toUpperCase() : "?"}
            </Avatar>
            {typeConfig && (
              <Chip
                label={typeLabel}
                size='small'
                variant='outlined'
                icon={
                  IconComponent ? (
                    <IconComponent sx={{ fontSize: 14 }} />
                  ) : undefined
                }
                color={typeConfig.color || "default"}
                sx={{
                  fontWeight: 500,
                  fontSize: "0.6875rem",
                  flexShrink: 0,
                  height: 22,
                  maxWidth: displayConfig.avatarSize <= 36 ? 104 : 120,
                  "& .MuiChip-label": {
                    maxWidth: displayConfig.avatarSize <= 36 ? 74 : 90,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  },
                  "& .MuiChip-icon": { ml: 0.5 }
                }}
              />
            )}
          </Box>

          <Tooltip title={user.name || ""} arrow>
            <Typography
              variant='h6'
              fontWeight={600}
              sx={{
                display: "-webkit-box",
                fontSize: "1rem",
                lineHeight: 1.25,
                minWidth: 0,
                overflow: "hidden",
                overflowWrap: "anywhere",
                textOverflow: "ellipsis",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: displayConfig.nameLineClamp
              }}
            >
              {user.name || "-"}
            </Typography>
          </Tooltip>

          <Tooltip title={user.email || ""} arrow>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.5,
                minHeight: 20,
                minWidth: 0
              }}
            >
              <EmailIcon
                sx={{
                  color: "text.secondary",
                  flexShrink: 0,
                  fontSize: 14
                }}
              />
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "0.8125rem",
                  minWidth: 0
                }}
              >
                {user.email || "-"}
              </Typography>
            </Box>
          </Tooltip>

          <Box
            sx={{
              mt: "auto",
              pt: displayConfig.footerPaddingTop,
              borderTop: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 0.5,
              minWidth: 0
            }}
          >
            <Chip
              label={capitalizeFirstLetter(getStatusLabel(status))}
              color={getStatusColor(status)}
              size='small'
              sx={{
                fontWeight: 500,
                fontSize: "0.75rem",
                flexShrink: 0,
                height: 24
              }}
            />
            {isSubsidiary && expiryDate && (
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  fontSize: "0.6875rem",
                  lineHeight: 1.2,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                Expiry: {formatDashedDateWithYear(expiryDate)}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserCard;
